import React from "react";
import { motion } from "framer-motion";
import {
  Wallet,
  Loader2,
  CheckCircle2,
  RefreshCw,
  XCircle,
  AlertTriangle,
  Clock,
  ShieldCheck,
  RotateCw,
} from "lucide-react";
import LegalShell from "@/components/digindia/LegalShell";

const STAGES = [
  {
    id: "payment",
    title: "Payment received",
    desc: "Your transaction lands in our gateway",
    icon: Wallet,
    accent: "#FF7A00",
  },
  {
    id: "processing",
    title: "Refund processing",
    desc: "Eligibility & invoice match (24h)",
    icon: Loader2,
    accent: "#FF9933",
    spin: true,
  },
  {
    id: "approval",
    title: "Approval",
    desc: "Founder sign-off on refund value",
    icon: CheckCircle2,
    accent: "#00C853",
  },
  {
    id: "refund",
    title: "Refund initiated",
    desc: "Money reflects in 5–7 working days",
    icon: RefreshCw,
    accent: "#138808",
  },
];

const ELIGIBLE = [
  "Project not started within agreed kickoff date",
  "Service spec materially undelivered after 14 days",
  "Duplicate or erroneous payment",
];

const NOT_REFUNDABLE = [
  "Domain registration & 3rd-party SaaS subscription fees",
  "Custom design hours already executed",
  "Work-in-progress builds delivered for review",
];

const Stage = ({ s, i }) => {
  const Icon = s.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, delay: i * 0.12 }}
      className="relative flex-1 min-w-[200px]"
      data-testid={`refund-stage-${s.id}`}
    >
      <div className="dark:glass-dark glass-light rounded-2xl border border-border p-5 md:p-6 relative overflow-hidden h-full">
        <div
          className="absolute -top-10 -right-10 h-32 w-32 rounded-full blur-3xl opacity-30"
          style={{ background: s.accent }}
        />
        <div
          className="h-11 w-11 rounded-xl grid place-items-center mb-4"
          style={{
            background: `${s.accent}1f`,
            border: `1px solid ${s.accent}40`,
          }}
        >
          <Icon
            className={`h-5 w-5 ${s.spin ? "animate-spin" : ""}`}
            style={{ color: s.accent }}
          />
        </div>
        <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-muted-foreground mb-1">
          Step 0{i + 1}
        </div>
        <div className="font-heading font-bold text-base md:text-lg tracking-tight mb-1">
          {s.title}
        </div>
        <div className="text-xs md:text-sm text-muted-foreground">{s.desc}</div>
      </div>
    </motion.div>
  );
};

export default function Refund() {
  return (
    <LegalShell
      kicker="Legal · Refunds"
      title="Refund Policy"
      accent="#FF9933"
      icon={RotateCw}
      intro="Fair, fast and predictable. Here's exactly how refunds flow at DIGINDIA PAYTECH — no fine print, no surprises."
      testid="refund-page"
    >
      {/* Timeline */}
      <div className="mb-14">
        <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-muted-foreground mb-5">
          Refund journey
        </div>
        <div className="relative">
          {/* Animated track */}
          <div className="hidden md:block absolute left-6 right-6 top-12 h-px bg-gradient-to-r from-[#FF7A00] via-[#FF9933] to-[#00C853]" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-5 relative">
            {STAGES.map((s, i) => (
              <Stage s={s} i={i} key={s.id} />
            ))}
          </div>
        </div>
      </div>

      {/* Highlight note */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="relative rounded-2xl border border-[#FF7A00]/40 bg-[#FF7A00]/5 p-5 md:p-6 mb-14 overflow-hidden"
        data-testid="refund-highlight"
      >
        <span className="absolute -left-2 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-[#FF7A00]/30 blur-xl animate-pulse" />
        <div className="relative flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-[#FF7A00]/20 grid place-items-center shrink-0">
            <AlertTriangle className="h-5 w-5 text-[#FF7A00]" />
          </div>
          <div className="space-y-1">
            <div className="font-heading font-bold text-base md:text-lg">
              Standard processing window: 5–7 working days
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Once approved, refunds reflect to the original payment instrument
              within 5–7 working days. UPI refunds are usually instant. Banks
              may add 2–3 days for credit cards.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Eligibility */}
      <div className="grid md:grid-cols-2 gap-5 md:gap-6 mb-14">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
          className="rounded-2xl dark:glass-dark glass-light border border-border p-6 md:p-7"
          data-testid="refund-eligible"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-xl bg-[#00C853]/15 border border-[#00C853]/40 grid place-items-center">
              <ShieldCheck className="h-5 w-5 text-[#00C853]" />
            </div>
            <h3 className="font-heading font-bold text-xl tracking-tight">
              Refund Eligibility
            </h3>
          </div>
          <ul className="space-y-3">
            {ELIGIBLE.map((t) => (
              <li key={t} className="flex items-start gap-3 text-sm md:text-base">
                <span className="mt-0.5 h-5 w-5 rounded-full bg-[#00C853]/20 grid place-items-center shrink-0">
                  <CheckCircle2 className="h-3.5 w-3.5 text-[#00C853]" />
                </span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55, delay: 0.08 }}
          className="rounded-2xl dark:glass-dark glass-light border border-border p-6 md:p-7"
          data-testid="refund-not-refundable"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-xl bg-[#FF512F]/15 border border-[#FF512F]/40 grid place-items-center">
              <XCircle className="h-5 w-5 text-[#FF512F]" />
            </div>
            <h3 className="font-heading font-bold text-xl tracking-tight">
              Non-refundable services
            </h3>
          </div>
          <ul className="space-y-3">
            {NOT_REFUNDABLE.map((t) => (
              <li key={t} className="flex items-start gap-3 text-sm md:text-base">
                <span className="mt-0.5 h-5 w-5 rounded-full bg-[#FF512F]/20 grid place-items-center shrink-0">
                  <XCircle className="h-3.5 w-3.5 text-[#FF512F]" />
                </span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>

      {/* Processing */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.55 }}
        className="rounded-2xl dark:glass-dark glass-light border border-border p-6 md:p-7"
        data-testid="refund-processing-info"
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="h-10 w-10 rounded-xl bg-[#FF9933]/15 border border-[#FF9933]/40 grid place-items-center">
            <Clock className="h-5 w-5 text-[#FF9933]" />
          </div>
          <h3 className="font-heading font-bold text-xl tracking-tight">
            Processing time
          </h3>
        </div>
        <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
          Once your refund request is received and approved, funds are
          released to the original payment method within{" "}
          <span className="text-foreground font-semibold">
            5–7 working days
          </span>
          . You will receive an email confirmation with the gateway reference
          number for your records.
        </p>
      </motion.div>

      <div className="mt-12 text-sm text-muted-foreground">
        Need to raise a refund request? Email{" "}
        <a className="text-[#FF9933] font-semibold" href="mailto:refunds@digindia.tech">
          refunds@digindia.tech
        </a>{" "}
        with your invoice number.
      </div>
    </LegalShell>
  );
}
